<template>
  <div v-if="eventData">
    <event-page-header :data="eventData" class="single-page__header" />

    <section-wrapper class="page-content__container">
      <nuxt-picture
        v-if="eventData.attributes.image.data"
        :alt="eventData.attributes.image.data.attributes.alternativeText"
        :img-attrs="{ class: 'picture__image' }"
        :src="useImageUrl(eventData.attributes.image, 'medium', 'large')"
        class="event-main-picture"
      />

      <single-page-content
        :content="eventData?.attributes?.text"
        class="md:px-40"
      >
        {{ eventData.attributes.introduction }}
      </single-page-content>

      <tags-list
        v-if="eventData.attributes.tags?.data.length"
        :tags="eventData.attributes.tags?.data"
        class="tags__list"
      />
    </section-wrapper>
  </div>
</template>

<script lang="ts" setup>
import type { Ref } from "vue";
import { useErrorTracking } from "~/modules/error/composables/useErrorTracking";
import EventPageHeader from "~/modules/events/components/EventPageHeader.vue";
import type { EventDto } from "~/modules/events/ts/dto/EventDto";
import type { Event } from "~/modules/events/ts/Event";
import SectionWrapper from "~/modules/shared/components/SectionWrapper.vue";
import SinglePageContent from "~/modules/shared/components/SinglePageContent.vue";
import TagsList from "~/modules/shared/components/tagsList/TagsList.vue";
import { useImageUrl } from "~/modules/shared/composables/useImageUrl";
import { CLIENT_ERROR_SLUGS } from "~/modules/shared/constants/ClientErrorSlugs";
import { usePageLocaleStore } from "~/modules/shared/stores/locale";
import type { StrapiLocale } from "~/node_modules/@nuxtjs/strapi/dist/runtime/types";

const pageLocaleStore = usePageLocaleStore();
const { locale } = useI18n();
const { findOne } = useStrapi4();
const { trackError } = useErrorTracking();
const route = useRoute();

const eventData: Ref<Event | undefined> = ref(undefined);

const { data, error } = await useAsyncData(
  `${route.params.slug}-event-data`,
  () =>
    findOne<EventDto>("slugify/slugs/event", route.params.slug as string, {
      populate: ["image", "localizations", "seo", "tags"],
      locale: locale.value as StrapiLocale,
    }),
);

if (data.value) {
  pageLocaleStore.pageLocales = data.value.data.attributes.localizations?.data;
  eventData.value = data.value.data;
} else {
  throw createError({
    fatal: import.meta.client,
    statusCode: 404,
    statusMessage: "Page Not Found",
  });
}

if (error.value) {
  trackError(CLIENT_ERROR_SLUGS.EVENT_INIT, error.value);
}

onUnmounted(() => {
  pageLocaleStore.pageLocales = undefined;
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/mixins";

.single-page__header {
  @apply mb-10;
}

.page-content__container {
  @apply mt-10 md:mt-20;
}

.event-main-picture {
  @apply w-full mb-10 md:mb-32 overflow-hidden;
  border-radius: 2.4rem;
  aspect-ratio: 16 / 9;

  :deep {
    .picture__image {
      @apply w-full h-full;
      object-fit: cover;
    }
  }
}

.tags__list {
  @apply mt-20 md:mt-32 md:px-40;
}
</style>
